import React, { FC } from "react";
import MainTable from "src/components/MainTable";
import { Service, ServiceOrder } from "src/types";
import { useServicesContext } from "src/providers/ServicesStore";
import { Trans } from "react-i18next";
import { UseTableRowProps } from "react-table";
import Preloader from "src/components/Preloader";
import LinkTo from "src/components/LinkTo";
import Typography from "@material-ui/core/Typography";
import format from "date-fns/format";

export const columns = () => {
  return [
    {
      Header: () => <Trans ns={"orders"} i18nKey="labels.table.service_id" />,
      id: "name",
      accessor: (ent: Partial<Service>) => ent.name,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) => (
        <LinkTo href={`services/details/${row.original.id}/summary`}>
          <Typography variant="body2" component={"span"}>
            {row.original.name}
          </Typography>
        </LinkTo>
      ),
    },
    {
      Header: () => <Trans ns={"orders"} i18nKey="labels.table.service_type" />,
      id: "serviceType",
      accessor: (ent: Partial<Service>) => ent?.serviceType?.displayText,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) => (
        <Typography variant="body2">{row.original.serviceType?.displayText}</Typography>
      ),
    },
    {
      Header: () => <Trans i18nKey="labels.table.seller" ns={"orders"} />,
      id: "seller",
      accessor: (ent: Partial<Service>) => ent?.seller?.displayText,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) => (
        <Typography variant="body2">{row.original.seller?.displayText}</Typography>
      ),
    },
    {
      Header: () => <Trans i18nKey="labels.table.delivery_date" ns={"orders"} />,
      id: "deliveryDate",
      accessor: (ent: Partial<Service>) => ent.deliveryDate,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) =>
        !!row.original.deliveryDate ? (
          <Typography variant="body2">
            {format(new Date(row.original.deliveryDate), "dd.MM.yyyy")}
          </Typography>
        ) : (
          <Typography variant="caption">none</Typography>
        ),
    },
    {
      Header: () => <Trans i18nKey="labels.table.workers_count" ns={"orders"} />,
      id: "workersCount",
      accessor: (ent: Partial<Service>) => ent.assignedWorkersCount,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) => (
        <Typography variant="body2">{row.original.assignedWorkersCount}</Typography>
      ),
    },
    {
      Header: () => <Trans i18nKey="labels.table.status" ns={"orders"} />,
      id: "statusColor",
      accessor: (ent: Partial<Service>) => ent.status?.displayText,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) =>
        !!row.original.status?.displayText ? (
          <span
            style={{
              textAlign: "center",
              width: "fit-content",
              padding: 3,
              borderRadius: 5,
              backgroundColor: row.original.statusColor,
            }}
          >
            {row.original.status?.displayText}
          </span>
        ) : (
          <Typography variant="caption">none</Typography>
        ),
    },
    {
      Header: () => <Trans i18nKey="labels.table.externalId" ns={"orders"} />,
      id: "externalId",
      accessor: (ent: Partial<Service>) => ent.externalId,
      Cell: ({ row }: { row: UseTableRowProps<Service> }) =>
        !!row.original.externalId ? (
          <Typography variant="body2">{row.original.externalId}</Typography>
        ) : (
          <Typography variant="caption">none</Typography>
        ),
    },
  ];
};

const SODetails: FC<{ data: ServiceOrder }> = ({ data }) => {
  const { servicesData } = useServicesContext();

  if (servicesData.loading) {
    return <Preloader is />;
  }

  return (
    <MainTable<Service> data={servicesData.selectedSO[data.id!]?.services} columnsData={columns} />
  );
};

export default React.memo(SODetails);
